import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import VLMSensorStateDisplay from '../VLM/VLMSensorStateDisplay';
import VLMPromptConfigDialog from '../VLM/VLMPromptConfigDialog';
import { 
  getVLMConfig, 
  updateVLMConfig, 
  getVLMEvents,
  createVLMConfig 
} from '../../api';

const DynamicVLMSensor = ({ sensorId, sensorData: propSensorData }) => {
  const [vlmConfig, setVlmConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load VLM configuration and events on component mount
  useEffect(() => {
    loadVLMConfig();
  }, [sensorId]);

  useEffect(() => {
    if (vlmConfig && vlmConfig.state === 'deployed') {
      loadVLMEvents();
    }
  }, [vlmConfig]);

  const loadVLMConfig = async () => {
    try {
      setLoading(true);
      const response = await getVLMConfig(sensorId);
      setVlmConfig(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading VLM config:', err);
      // If config doesn't exist, show default state
      if (err.response?.status === 404) {
        setVlmConfig({
          state: 'configured',
          promptConfigs: [{
            prompt: '',
            title: 'No Configuration',
            category: 'Unconfigured',
            isCustom: true
          }],
          trainingProgress: {
            currentStep: 0,
            totalSteps: 5
          },
          deploymentInfo: {
            eventsDetected: 0
          }
        });
      } else {
        setError('Failed to load VLM configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVLMEvents = async () => {
    try {
      const response = await getVLMEvents(sensorId, { page: 1, limit: 10 });
      setEvents(response.data.events || []);
    } catch (err) {
      console.error('Error loading VLM events:', err);
      setEvents([]);
    }
  };

  const handleReconfigure = () => {
    setShowConfigDialog(true);
  };

  const handlePromptSave = async (promptConfigs) => {
    setLoading(true);
    try {
      if (vlmConfig && vlmConfig._id) {
        // Update existing configuration
        const response = await updateVLMConfig(sensorId, {
          promptConfigs,
          reason: 'User reconfiguration',
          userId: 'current-user' // TODO: Get from auth context
        });
        setVlmConfig(response.data.config);
      } else {
        // Create new configuration
        const response = await createVLMConfig({
          entitySensorId: sensorId,
          entityId: propSensorData?.entityId, // TODO: Get from props or context
          appId: propSensorData?.appId, // TODO: Get from props or context
          promptConfigs
        });
        setVlmConfig(response.data.config);
      }
      
      setShowConfigDialog(false);
      setError(null);
    } catch (error) {
      console.error('Error saving prompt configuration:', error);
      setError('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPrompt = () => {
    // This could open a detailed view of the current prompt
    console.log('Viewing prompt configuration:', vlmConfig?.promptConfig);
  };

  const handleRefresh = async () => {
    await loadVLMConfig();
    if (vlmConfig && vlmConfig.state === 'deployed') {
      await loadVLMEvents();
    }
  };

  const getEventStatusColor = (status) => {
    switch (status) {
      case 'active': return 'error';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const getEventIcon = (status) => {
    switch (status) {
      case 'active': return <WarningIcon />;
      case 'resolved': return <CheckCircleIcon />;
      default: return <AccessTimeIcon />;
    }
  };

  if (loading && !vlmConfig) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading VLM sensor configuration...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={
          <Button onClick={loadVLMConfig}>Retry</Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Sensor State Display */}
        <Grid item xs={12}>
          <VLMSensorStateDisplay
            sensorData={vlmConfig}
            onReconfigure={handleReconfigure}
            onViewPrompt={handleViewPrompt}
            onRefresh={handleRefresh}
          />
        </Grid>

        {/* Events Display - Only show when deployed */}
        {vlmConfig?.state === 'deployed' && events.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Events
                </Typography>
                <List>
                  {events.map((event, index) => (
                    <React.Fragment key={event.id}>
                      <ListItem>
                        <ListItemIcon>
                          {getEventIcon(event.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">
                                {event.type}
                              </Typography>
                              <Chip 
                                label={`${Math.round(event.confidence * 100)}% confidence`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip 
                                label={event.status}
                                size="small"
                                color={getEventStatusColor(event.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {event.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(event.timestamp).toLocaleString()} • {event.location}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < events.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* No Events Message */}
        {vlmConfig?.state === 'deployed' && events.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              No events detected yet. The VLM sensor is actively monitoring for: "{vlmConfig.promptConfig?.title}"
            </Alert>
          </Grid>
        )}

        {/* Training/Configuration Messages */}
        {vlmConfig?.state === 'configured' && (
          <Grid item xs={12}>
            <Alert severity="info">
              {vlmConfig.promptConfig ? 
                'Sensor configured and ready for training. Training will begin shortly and typically takes 2-7 days.' :
                'Please configure the VLM prompt to start training.'
              }
            </Alert>
          </Grid>
        )}

        {vlmConfig?.state === 'intraining' && (
          <Grid item xs={12}>
            <Alert severity="warning">
              Model training in progress. The sensor will be deployed automatically once training is complete.
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Prompt Configuration Dialog */}
      <VLMPromptConfigDialog
        open={showConfigDialog}
        onClose={() => setShowConfigDialog(false)}
        onSave={handlePromptSave}
        initialPrompts={vlmConfig?.promptConfigs || []}
        sensorName="Dynamic VLM Sensor"
      />
    </Box>
  );
};

export default DynamicVLMSensor;
